import { ZuruflexApiError } from '@zuruflex/api-client';
import { formatDateIn, formatDateTimeIn, formatMoneyIn, toLocale } from '@zuruflex/i18n';
import type { Customer, PromotionDetail, PromotionRedemptionRow } from '@zuruflex/types';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Combobox,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  FormField,
  Input,
  PageHeader,
  Skeleton,
  StatTile,
  Textarea,
  type ColumnDef,
  type ComboboxOption,
} from '@zuruflex/ui';
import { formatPhone } from '@zuruflex/utils';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ChevronLeft,
  Coins,
  Pause,
  Pencil,
  Play,
  ReceiptText,
  TicketPercent,
  Undo2,
  UserPlus,
  WandSparkles,
  X,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { PromotionDialog } from '../components/PromotionDialog.js';
import { api } from '../lib/api.js';
import {
  promotionState,
  promotionUses,
  promotionValue,
  promotionWindow,
  REDEMPTION_VARIANT,
  STATE_VARIANT,
} from '../lib/promotions.js';
import { useTableLabels } from '../lib/table-labels.js';

type Panel = 'edit' | 'archive' | 'codes' | 'assign' | null;

/**
 * One promotion: what it gives, what it has cost, and who has used it.
 *
 * Every action that changes it lives in the header — pause, resume, archive,
 * edit — and the two that only make sense for some promotions (single-use
 * codes for a code promo, named customers for an assigned one) appear only
 * on those.
 */
export function PromotionDetailPage() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const tableLabels = useTableLabels();
  const queryClient = useQueryClient();
  const [panel, setPanel] = useState<Panel>(null);

  const promo = useQuery({
    queryKey: ['promotion', id],
    queryFn: () => api.getPromotion(id),
  });
  const redemptions = useQuery({
    queryKey: ['promotion', id, 'redemptions'],
    queryFn: () => api.promotionRedemptions(id, { perPage: 100 }),
    placeholderData: keepPreviousData,
  });

  // Names for the targeting lists, which the promotion carries as ids.
  const partners = useQuery({
    queryKey: ['partners', 'all'],
    queryFn: () => api.listPartners({ perPage: 100 }),
  });
  const locations = useQuery({ queryKey: ['locations'], queryFn: () => api.listLocations() });
  const vehicles = useQuery({
    queryKey: ['fleet', 'promotion-targets', promo.data?.partnerId ?? ''],
    queryFn: () =>
      api.listVehicles({
        perPage: 100,
        ...(promo.data?.partnerId ? { partnerId: promo.data.partnerId } : {}),
      }),
    enabled: (promo.data?.vehicleIds.length ?? 0) > 0,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['promotion', id] });
    void queryClient.invalidateQueries({ queryKey: ['promotions'] });
  };

  const setStatus = useMutation({
    mutationFn: (action: 'pause' | 'resume') =>
      action === 'pause' ? api.pausePromotion(id) : api.resumePromotion(id),
    onSuccess: refresh,
  });

  const columns = useMemo<ColumnDef<PromotionRedemptionRow>[]>(
    () => [
      {
        id: 'reference',
        header: t('common:field.reference'),
        cell: ({ row }) => (
          <Link
            to={`/bookings/${row.original.booking.reference}`}
            className="font-mono font-semibold underline-offset-2 hover:underline"
          >
            {row.original.booking.reference}
          </Link>
        ),
      },
      {
        id: 'customer',
        header: t('bookings.customer'),
        cell: ({ row }) =>
          row.original.customer.fullName ?? (
            <span className="text-text-muted">{t('customers.unnamed')}</span>
          ),
      },
      {
        id: 'dates',
        header: t('promotions.detail.hire'),
        cell: ({ row }) => (
          <span className="text-sm text-text-muted">
            {formatDateIn(row.original.booking.pickupAt, locale)} →{' '}
            {formatDateIn(row.original.booking.dropoffAt, locale)}
          </span>
        ),
      },
      {
        accessorKey: 'discountAmount',
        header: t('promotions.detail.discount'),
        cell: ({ row }) => (
          <span className="font-semibold tabular-nums">
            {formatMoneyIn(row.original.discountAmount, row.original.currency, locale)}
          </span>
        ),
      },
      {
        id: 'total',
        header: t('common:field.total'),
        cell: ({ row }) => (
          <span className="tabular-nums text-text-muted">
            {formatMoneyIn(row.original.booking.total, row.original.currency, locale)}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: t('promotions.detail.use'),
        cell: ({ row }) => (
          <Badge variant={REDEMPTION_VARIANT[row.original.status]}>
            {t(`promotions.redemption.${row.original.status}`)}
          </Badge>
        ),
      },
      {
        id: 'bookingStatus',
        header: t('promotions.detail.bookingStatus'),
        cell: ({ row }) => (
          <span className="text-sm text-text-muted">
            {t(`common:bookingStatus.${row.original.booking.status}`)}
          </span>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: t('promotions.detail.when'),
        cell: ({ row }) => (
          <span className="text-sm text-text-muted">
            {formatDateTimeIn(row.original.createdAt, locale)}
          </span>
        ),
      },
    ],
    [t, locale],
  );

  if (promo.error) {
    return <ErrorState error={promo.error} onRetry={() => void promo.refetch()} />;
  }
  if (promo.isPending) return <Skeleton className="h-96 w-full" />;

  const detail = promo.data;
  const state = promotionState(detail);
  const archived = detail.status === 'ARCHIVED';
  const { stats } = detail;

  const partnerName = (partnerId: string) =>
    partners.data?.items.find((partner) => partner.id === partnerId)?.displayName ?? '—';
  const namesOf = (ids: string[], lookup: (id: string) => string | undefined) =>
    ids.length === 0 ? t('promotions.detail.all') : ids.map((key) => lookup(key) ?? '—').join(', ');

  return (
    <>
      <PageHeader
        eyebrow={
          <Link
            to="/promotions"
            className="inline-flex items-center gap-1 text-sm text-text-muted transition-colors hover:text-text"
          >
            <ChevronLeft className="size-4" />
            {t('promotions.detail.back')}
          </Link>
        }
        title={detail.name}
        description={
          <span className="inline-flex flex-wrap items-center gap-2">
            {detail.code ? (
              <span className="font-mono font-semibold text-text">{detail.code}</span>
            ) : (
              <span>{t('promotions.trigger.AUTOMATIC')}</span>
            )}
            <span aria-hidden="true">·</span>
            <span>
              {detail.funder === 'PARTNER'
                ? t('promotions.paidByPartner', { partner: partnerName(detail.partnerId ?? '') })
                : t('promotions.paidByZuruflex')}
            </span>
            <Badge variant={STATE_VARIANT[state]}>{t(`promotions.state.${state}`)}</Badge>
          </span>
        }
        action={
          archived ? null : (
            <div className="flex flex-wrap items-center gap-2">
              {detail.audience === 'ASSIGNED' ? (
                <Button size="sm" variant="outline" onClick={() => setPanel('assign')}>
                  <UserPlus />
                  {t('promotions.detail.assign')}
                </Button>
              ) : null}
              {detail.trigger === 'CODE' ? (
                <Button size="sm" variant="outline" onClick={() => setPanel('codes')}>
                  <WandSparkles />
                  {t('promotions.detail.generate')}
                </Button>
              ) : null}
              <Button size="sm" variant="outline" onClick={() => setPanel('edit')}>
                <Pencil />
                {t('promotions.detail.edit')}
              </Button>
              {detail.status === 'PAUSED' ? (
                <Button
                  size="sm"
                  isLoading={setStatus.isPending}
                  onClick={() => setStatus.mutate('resume')}
                >
                  <Play />
                  {t('promotions.detail.resume')}
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  isLoading={setStatus.isPending}
                  onClick={() => setStatus.mutate('pause')}
                >
                  <Pause />
                  {t('promotions.detail.pause')}
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => setPanel('archive')}>
                <Archive />
                {t('promotions.detail.archive')}
              </Button>
            </div>
          )
        }
      />

      {setStatus.error ? <ErrorState error={setStatus.error} className="mb-6" /> : null}
      {archived ? <Alert className="mb-6">{t('promotions.detail.archivedNote')}</Alert> : null}

      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatTile
            label={t('promotions.detail.stat.uses')}
            value={promotionUses(detail, t)}
            hint={t('promotions.detail.stat.usesHint', {
              reserved: stats.reserved,
              confirmed: stats.confirmed,
            })}
            icon={<TicketPercent className="size-4" />}
            accent
          />
          <StatTile
            label={t('promotions.detail.stat.discount')}
            value={formatMoneyIn(stats.totalDiscount, detail.currency, locale)}
            hint={t('promotions.detail.stat.discountHint')}
            icon={<Coins className="size-4" />}
          />
          <StatTile
            label={t('promotions.detail.stat.revenue')}
            value={formatMoneyIn(stats.revenue, detail.currency, locale)}
            hint={t('promotions.detail.stat.revenueHint', { count: stats.bookings })}
            icon={<ReceiptText className="size-4" />}
          />
          <StatTile
            label={t('promotions.detail.stat.released')}
            value={String(stats.released)}
            hint={t('promotions.detail.stat.releasedHint')}
            icon={<Undo2 className="size-4" />}
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('promotions.detail.terms')}</CardTitle>
          </CardHeader>
          <CardContent>
            {detail.description ? (
              <p className="mb-5 text-sm text-text-muted">{detail.description}</p>
            ) : null}
            <dl className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3 xl:grid-cols-4">
              <Field label={t('promotions.column.value')}>
                {promotionValue(detail, t, locale)}
              </Field>
              <Field label={t('promotions.column.audience')}>
                {t(`promotions.audience.${detail.audience}`)}
                {detail.audience === 'ASSIGNED'
                  ? ` · ${t('promotions.detail.assignedCount', { count: detail.assignedCount })}`
                  : ''}
              </Field>
              <Field label={t('promotions.column.window')}>
                {promotionWindow(detail, t, locale)}
              </Field>
              <Field label={t('promotions.form.maxPerCustomer')}>
                {String(detail.maxPerCustomer)}
              </Field>
              <Field label={t('promotions.form.minDays')}>
                {detail.minDays === null ? '—' : t('common:unit.day', { count: detail.minDays })}
              </Field>
              <Field label={t('promotions.form.minSubtotal')}>
                {detail.minSubtotal === null
                  ? '—'
                  : formatMoneyIn(detail.minSubtotal, detail.currency, locale)}
              </Field>
              <Field label={t('promotions.form.country')}>
                {detail.countryCode ?? t('promotions.form.anyCountry')}
              </Field>
              <Field label={t('promotions.column.source')}>
                {t(`promotions.source.${detail.source}`)}
              </Field>
              <Field label={t('promotions.form.vehicles')} wide>
                {namesOf(detail.vehicleIds, (key) => {
                  const car = vehicles.data?.items.find((vehicle) => vehicle.id === key);
                  return car ? `${car.make} ${car.model} (${car.plateNumber})` : undefined;
                })}
              </Field>
              <Field label={t('promotions.form.locations')} wide>
                {namesOf(
                  detail.locationIds,
                  (key) => locations.data?.find((location) => location.id === key)?.name,
                )}
              </Field>
              {detail.funder === 'ZURUFLEX' ? (
                <Field label={t('promotions.form.partners')} wide>
                  {namesOf(detail.partnerIds, partnerName)}
                </Field>
              ) : null}
            </dl>
          </CardContent>
        </Card>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-text">{t('promotions.detail.redemptions')}</h2>
          {redemptions.error ? (
            <ErrorState error={redemptions.error} onRetry={() => void redemptions.refetch()} />
          ) : (
            <DataTable
              labels={tableLabels}
              columns={columns}
              data={redemptions.data?.items ?? []}
              isLoading={redemptions.isPending}
              emptyMessage={t('promotions.detail.noRedemptions')}
            />
          )}
        </section>
      </div>

      {panel === 'edit' ? (
        <PromotionDialog promotion={detail} onClose={() => setPanel(null)} />
      ) : null}
      {panel === 'archive' ? (
        <ArchiveDialog promo={detail} onClose={() => setPanel(null)} onDone={refresh} />
      ) : null}
      {panel === 'codes' ? (
        <GenerateCodesDialog promo={detail} onClose={() => setPanel(null)} />
      ) : null}
      {panel === 'assign' ? (
        <AssignDialog promo={detail} onClose={() => setPanel(null)} onDone={refresh} />
      ) : null}
    </>
  );
}

function Field({
  label,
  children,
  wide = false,
}: {
  label: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={wide ? 'col-span-2 sm:col-span-3 xl:col-span-4' : undefined}>
      <dt className="text-[11px] font-bold uppercase tracking-widest text-text-muted">{label}</dt>
      <dd className="mt-1 text-sm font-medium">{children}</dd>
    </div>
  );
}

function MutationError({ error }: { error: unknown }) {
  const { t } = useTranslation('common');
  if (!error) return null;
  return (
    <Alert variant="danger" className="mt-4">
      {error instanceof ZuruflexApiError ? error.message : t('error.unexpected')}
    </Alert>
  );
}

/** Archiving is DELETE: it cannot be undone from here, so it is confirmed. */
function ArchiveDialog({
  promo,
  onClose,
  onDone,
}: {
  promo: PromotionDetail;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const archive = useMutation({
    mutationFn: () => api.archivePromotion(promo.id),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('promotions.detail.archiveTitle', { name: promo.name })}</DialogTitle>
          <DialogDescription>{t('promotions.detail.archiveBody')}</DialogDescription>
        </DialogHeader>
        <MutationError error={archive.error} />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common:action.cancel')}
          </Button>
          <Button
            variant="destructive"
            isLoading={archive.isPending}
            onClick={() => archive.mutate()}
          >
            {t('promotions.detail.archive')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Single-use copies of this promotion, one code each — for handing out at
 * an event, or to a partner's corporate client, where one shared code
 * would be passed on and used up by strangers.
 */
function GenerateCodesDialog({ promo, onClose }: { promo: PromotionDetail; onClose: () => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();
  const [count, setCount] = useState('25');
  const [prefix, setPrefix] = useState(promo.code ?? '');
  const [copied, setCopied] = useState(false);

  const generate = useMutation({
    mutationFn: () =>
      api.generatePromotionCodes(promo.id, {
        count: Math.round(Number(count)),
        ...(prefix.trim() ? { prefix: prefix.trim().toUpperCase() } : {}),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['promotions'] }),
  });

  const codes = (generate.data?.items ?? []).map((item) => item.code ?? '').filter(Boolean);
  const countValid = Number.isInteger(Number(count)) && Number(count) >= 1 && Number(count) <= 500;

  async function copy() {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
    } catch {
      // Clipboard refused (an insecure origin, a denied permission): the
      // codes are still selectable in the box.
    }
  }

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('promotions.detail.generateTitle')}</DialogTitle>
          <DialogDescription>{t('promotions.detail.generateBody')}</DialogDescription>
        </DialogHeader>

        {codes.length > 0 ? (
          <div className="space-y-3">
            <p className="text-sm font-medium">
              {t('promotions.detail.generated', { count: codes.length })}
            </p>
            <Textarea
              readOnly
              rows={8}
              className="font-mono text-xs"
              value={codes.join('\n')}
              onFocus={(event) => event.currentTarget.select()}
            />
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label={t('promotions.detail.count')} hint={t('promotions.detail.countHint')}>
              {(field) => (
                <Input
                  {...field}
                  type="number"
                  min={1}
                  max={500}
                  step={1}
                  value={count}
                  onChange={(event) => setCount(event.target.value)}
                />
              )}
            </FormField>
            <FormField
              label={t('promotions.detail.prefix')}
              hint={t('promotions.detail.prefixHint')}
            >
              {(field) => (
                <Input
                  {...field}
                  className="font-mono uppercase"
                  maxLength={20}
                  value={prefix}
                  onChange={(event) => setPrefix(event.target.value.toUpperCase())}
                />
              )}
            </FormField>
          </div>
        )}

        <MutationError error={generate.error} />

        <DialogFooter>
          {codes.length > 0 ? (
            <>
              <Button variant="outline" onClick={() => void copy()}>
                {copied ? t('promotions.detail.copied') : t('promotions.detail.copy')}
              </Button>
              <Button onClick={onClose}>{t('common:action.close')}</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose}>
                {t('common:action.cancel')}
              </Button>
              <Button
                isLoading={generate.isPending}
                disabled={!countValid}
                onClick={() => generate.mutate()}
              >
                {t('promotions.detail.generate')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Names the customers an ASSIGNED promotion is for. Adding only; nobody is removed here. */
function AssignDialog({
  promo,
  onClose,
  onDone,
}: {
  promo: PromotionDetail;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<Customer[]>([]);

  const customers = useQuery({
    queryKey: ['customers', 'search', q],
    queryFn: () => api.listCustomers({ perPage: 50, q: q.trim() || undefined }),
    placeholderData: keepPreviousData,
  });

  const options = useMemo<ComboboxOption[]>(() => {
    const chosen = new Set(picked.map((customer) => customer.id));
    return (customers.data?.items ?? [])
      .filter((customer) => !chosen.has(customer.id))
      .map((customer) => ({
        value: customer.id,
        label: customer.fullName ?? t('customers.unnamed'),
        description: customer.phone ? formatPhone(customer.phone) : (customer.email ?? undefined),
      }));
  }, [customers.data, picked, t]);

  const assign = useMutation({
    mutationFn: () =>
      api.assignPromotion(
        promo.id,
        picked.map((customer) => customer.id),
      ),
    onSuccess: () => onDone(),
  });

  const nameOf = (customer: Customer) => customer.fullName ?? t('customers.unnamed');

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('promotions.detail.assignTitle')}</DialogTitle>
          <DialogDescription>
            {t('promotions.detail.assignBody', { count: promo.assignedCount })}
          </DialogDescription>
        </DialogHeader>

        {assign.data ? (
          <Alert variant="success">
            {t('promotions.detail.assignedDone', { count: assign.data.assigned })}
          </Alert>
        ) : (
          <div className="space-y-3">
            <FormField label={t('promotions.detail.filterCustomers')}>
              {(field) => (
                <Input
                  {...field}
                  value={q}
                  placeholder={t('customers.searchPlaceholder')}
                  onChange={(event) => setQ(event.target.value)}
                />
              )}
            </FormField>
            <FormField label={t('promotions.detail.customers')}>
              {(field) => (
                <Combobox
                  id={field.id}
                  options={options}
                  value=""
                  onChange={(customerId) => {
                    const customer = customers.data?.items.find((row) => row.id === customerId);
                    if (customer) setPicked((current) => [...current, customer]);
                  }}
                  placeholder={t('promotions.detail.pickCustomer')}
                  searchPlaceholder={t('promotions.form.search')}
                  emptyMessage={t('promotions.form.nothing')}
                />
              )}
            </FormField>
            {picked.length > 0 ? (
              <ul className="flex flex-wrap gap-1.5">
                {picked.map((customer) => (
                  <li
                    key={customer.id}
                    className="inline-flex items-center gap-1 rounded-pill bg-surface-sunken py-1 pl-3 pr-1 text-xs font-medium"
                  >
                    {nameOf(customer)}
                    <button
                      type="button"
                      onClick={() =>
                        setPicked((current) => current.filter((row) => row.id !== customer.id))
                      }
                      aria-label={t('promotions.form.remove', { name: nameOf(customer) })}
                      className="inline-flex size-5 items-center justify-center rounded-full text-text-muted hover:bg-surface-hover hover:text-text"
                    >
                      <X className="size-3" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )}

        <MutationError error={assign.error} />

        <DialogFooter>
          {assign.data ? (
            <Button onClick={onClose}>{t('common:action.close')}</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose}>
                {t('common:action.cancel')}
              </Button>
              <Button
                isLoading={assign.isPending}
                disabled={picked.length === 0}
                onClick={() => assign.mutate()}
              >
                {t('promotions.detail.assignSubmit', { count: picked.length })}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
